import { ComponentProps, forwardRef, ReactNode } from 'react'
import { Badge } from '../shadcn'
import { cn, Color } from '../../lib/utils'
import { useMemo } from 'react'

type Props = {
  children: ReactNode
  className?: string
  color?: Color
  variant?: ComponentProps<typeof Badge>['variant']
} & ComponentProps<typeof Badge>

const variantMap = (color: Color, variant: ComponentProps<typeof Badge>['variant']) => {
  return {
    default: `bg-${color}-8`,
    secondary: `bg-${color}-3 text-${color}-10`,
    destructive: `bg-${color}-2 text-${color}-10`,
    outline: `bg-${color}-2 text-${color}-10 border-${color}-7`,
  }[variant ?? 'default']
}

const getColorVariant = (color: Color, variant: ComponentProps<typeof Badge>['variant']): string => {
  return variantMap(color, variant)
}

export const ColoredBadge = forwardRef<HTMLDivElement, Props>(
  ({ children, className, color, variant, ...props }, ref) => {
    const colorVariant = useMemo(() => getColorVariant(color ?? 'gray', variant), [color, variant])
    return (
      <Badge
        ref={ref}
        className={cn(colorVariant, 'rounded-sm px-1.5 shadow-none hover:shadow-none', className)}
        variant={variant}
        {...props}
      >
        {children}
      </Badge>
    )
  }
)

ColoredBadge.displayName = 'ColoredBadge'
